import React, { useEffect, useState } from 'react';
import {
  Plus,
  Eye,
  Trash2,
  RefreshCw,
  FileText,
  TrendingUp,
  Smile,
  Meh,
  Frown,
  Download,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { sentimentAnalysisApi, eventsApi, adminApi } from '../../services/api';
import { SentimentAnalysisReport, Event, AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const SentimentAnalysis: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<SentimentAnalysisReport[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState<{ eventId?: string; status?: 'generating' | 'completed' | 'failed' }>({});

  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<SentimentAnalysisReport | null>(null);
  const [rowDetailsReport, setRowDetailsReport] = useState<SentimentAnalysisReport | null>(null);

  // Form state replacing Form.useForm()
  const [formClientId, setFormClientId] = useState('');
  const [formEventId, setFormEventId] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formErrors, setFormErrors] = useState<{ clientId?: string; eventId?: string }>({});

  // Download dropdown state
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState<string | null>(null);
  const [viewDownloadDropdownOpen, setViewDownloadDropdownOpen] = useState(false);

  // Delete confirm state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const resetForm = () => {
    setFormClientId('');
    setFormEventId('');
    setFormStartDate('');
    setFormEndDate('');
    setFormErrors({});
  };

  // Fetch reports
  const fetchReports = async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const response = await sentimentAnalysisApi.list({
        page,
        limit,
        ...filters,
      });
      const data = response.data?.data || [];
      const pag = response.data?.pagination || { page, limit, total: data.length };
      setReports(data);
      setPagination(pag);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to fetch sentiment analysis reports');
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients (admin only)
  const fetchClients = async () => {
    try {
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        const users = response.data.data.users || [];
        setClients(users);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  // Fetch events for dropdown
  // Clients automatically see only their events (backend handles this via req.user)
  // Admins can filter by clientId if provided
  const fetchEvents = async () => {
    try {
      const params: any = { limit: 100 };
      // Only pass clientId if admin is filtering by a specific client
      // For clients, backend automatically filters by req.user.id
      if (isAdmin && selectedClientId) {
        params.clientId = selectedClientId;
      }
      const response = await eventsApi.getAll(params);
      const data = response.data?.data || [];
      setEvents(data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchClients();
    } else {
      fetchEvents();
    }
  }, [isAdmin]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchEvents();
  }, [selectedClientId, isAdmin]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReports(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Generate new report
  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { clientId?: string; eventId?: string } = {};
    if (isAdmin && !formClientId) errors.clientId = 'Please select a client';
    if (!formEventId) errors.eventId = 'Please select an event';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const payload = {
        eventId: formEventId,
        generatedBy: user?._id || 'admin', // Use authenticated user ID
        startDate: formStartDate ? new Date(formStartDate).toISOString() : undefined,
        endDate: formEndDate ? new Date(formEndDate).toISOString() : undefined,
      };

      const response = await sentimentAnalysisApi.generate(payload);
      toast.success(response.data?.data?.message || 'Report generation started');
      setGenerateModalVisible(false);
      resetForm();
      fetchReports(1, pagination.limit);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate report');
    }
  };

  // View report details
  const handleViewReport = async (reportId: string) => {
    try {
      const response = await sentimentAnalysisApi.getById(reportId);
      setSelectedReport(response.data?.data || null);
      setViewModalVisible(true);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to fetch report details');
    }
  };

  // Delete report
  const handleDeleteReport = async (reportId: string) => {
    try {
      await sentimentAnalysisApi.delete(reportId);
      toast.success('Report deleted successfully');
      setDeleteConfirmId(null);
      fetchReports(pagination.page, pagination.limit);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete report');
    }
  };

  // Download report
  const handleDownloadReport = async (reportId: string, format: 'markdown' | 'pdf' | 'docx') => {
    try {
      const response = await sentimentAnalysisApi.download(reportId, format);

      // Create blob from response
      const blob = new Blob([response.data], {
        type:
          format === 'markdown'
            ? 'text/markdown'
            : format === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sentiment-analysis-${reportId}.${format === 'markdown' ? 'md' : format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Report downloaded as ${format.toUpperCase()}`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to download report');
    }
  };

  // Get sentiment icon and color
  const getSentimentDisplay = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          icon: <Smile className="w-4 h-4" />,
          color: 'green',
          tagClass: 'bg-green-100 text-green-800 border-green-200',
          textClass: 'text-green-600',
          label: 'Positive',
        };
      case 'negative':
        return {
          icon: <Frown className="w-4 h-4" />,
          color: 'red',
          tagClass: 'bg-red-100 text-red-800 border-red-200',
          textClass: 'text-red-600',
          label: 'Negative',
        };
      case 'neutral':
        return {
          icon: <Meh className="w-4 h-4" />,
          color: 'blue',
          tagClass: 'bg-blue-100 text-blue-800 border-blue-200',
          textClass: 'text-blue-600',
          label: 'Neutral',
        };
      case 'mixed':
        return {
          icon: <TrendingUp className="w-4 h-4" />,
          color: 'orange',
          tagClass: 'bg-orange-100 text-orange-800 border-orange-200',
          textClass: 'text-orange-600',
          label: 'Mixed',
        };
      default:
        return {
          icon: <Meh className="w-4 h-4" />,
          color: 'default',
          tagClass: 'bg-gray-100 text-gray-800 border-gray-200',
          textClass: 'text-gray-600',
          label: sentiment,
        };
    }
  };

  const getStatusTagClass = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'generating') return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getScoreColorClass = (score: number) => {
    if (score > 0.3) return 'text-green-600';
    if (score < -0.3) return 'text-red-600';
    return 'text-gray-800';
  };

  // Pagination helpers
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">AI Sentiment Analysis Reports</h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate comprehensive AI-powered sentiment analysis reports based on conversations,
            feedback, and knowledge base data
          </p>
        </div>
        {isAdmin && (
          <button
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            onClick={() => setGenerateModalVisible(true)}
          >
            <Plus className="w-4 h-4" />
            Generate Report
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-center">
          {isAdmin && (
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              value={selectedClientId || ''}
              onChange={(e) => {
                const val = e.target.value || null;
                setSelectedClientId(val);
                setFilters((f) => ({ ...f, eventId: undefined }));
              }}
            >
              <option value="">Select Client</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isAdmin && !selectedClientId}
            value={filters.eventId || ''}
            onChange={(e) => setFilters((f) => ({ ...f, eventId: e.target.value || undefined }))}
          >
            <option value="">Filter by Knowledge Assistant</option>
            {events.map((e) => (
              <option key={e._id} value={e._id}>
                {e.name}
              </option>
            ))}
          </select>
          <select
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
            value={filters.status || ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value as 'generating' | 'completed' | 'failed') || undefined,
              }))
            }
          >
            <option value="">Filter by Status</option>
            <option value="generating">Generating</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-sm text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => fetchReports(1, pagination.limit)}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FileText className="w-12 h-12 mb-3" />
            <p className="text-sm">No reports found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-28">Report ID</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Knowledge Assistant</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-44">Generated</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-48">Date Range</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-36">Overall Sentiment</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Score</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-28">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 w-52">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reports.map((record) => {
                    const sentimentDisplay = getSentimentDisplay(record.overallSentiment);
                    return (
                      <tr key={record.reportId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">
                            {record.reportId}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-gray-800 truncate max-w-[200px]">
                          {record.eventName}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {dayjs(record.generatedAt).format('YYYY-MM-DD HH:mm')}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {dayjs(record.analysisDateRange.startDate).format('MMM D')} -{' '}
                          {dayjs(record.analysisDateRange.endDate).format('MMM D, YYYY')}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sentimentDisplay.tagClass}`}
                          >
                            {sentimentDisplay.icon}
                            {sentimentDisplay.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-semibold ${getScoreColorClass(record.sentimentScore)}`}>
                            {record.sentimentScore.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusTagClass(record.status)}`}
                          >
                            {record.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              className="inline-flex items-center gap-1 px-2 py-1 text-gray-700 hover:text-gray-900 text-xs"
                              onClick={() => setRowDetailsReport(record)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Details
                            </button>
                            <button
                              className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:text-blue-800 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                              disabled={record.status !== 'completed'}
                              onClick={() => handleViewReport(record.reportId)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>

                            {/* Download dropdown */}
                            <div className="relative">
                              <button
                                className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:text-blue-800 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                disabled={record.status !== 'completed'}
                                onClick={() =>
                                  setDownloadDropdownOpen(
                                    downloadDropdownOpen === record.reportId ? null : record.reportId
                                  )
                                }
                              >
                                <Download className="w-3.5 h-3.5" />
                                Download
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              {downloadDropdownOpen === record.reportId && (
                                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                                  {(['markdown', 'pdf', 'docx'] as const).map((fmt) => (
                                    <button
                                      key={fmt}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                                      onClick={() => {
                                        setDownloadDropdownOpen(null);
                                        handleDownloadReport(record.reportId, fmt);
                                      }}
                                    >
                                      <FileText className="w-3.5 h-3.5" />
                                      Download as {fmt.toUpperCase()}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Delete with inline confirm */}
                            {deleteConfirmId === record.reportId ? (
                              <span className="inline-flex items-center gap-1 text-xs">
                                <span className="text-gray-600">Delete?</span>
                                <button
                                  className="px-1.5 py-0.5 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                                  onClick={() => handleDeleteReport(record.reportId)}
                                >
                                  Yes
                                </button>
                                <button
                                  className="px-1.5 py-0.5 border border-gray-300 text-gray-600 rounded text-xs hover:bg-gray-50"
                                  onClick={() => setDeleteConfirmId(null)}
                                >
                                  No
                                </button>
                              </span>
                            ) : (
                              <button
                                className="inline-flex items-center gap-1 px-2 py-1 text-red-500 hover:text-red-700 text-xs"
                                onClick={() => setDeleteConfirmId(record.reportId)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                <span>Total {pagination.total} reports</span>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchReports(pagination.page - 1, pagination.limit)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {pagination.page} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={pagination.page >= totalPages}
                    onClick={() => fetchReports(pagination.page + 1, pagination.limit)}
                  >
                    Next
                  </button>
                  <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={pagination.limit}
                    onChange={(e) => fetchReports(1, Number(e.target.value))}
                  >
                    {[10, 20, 50].map((s) => (
                      <option key={s} value={s}>
                        {s} / page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate Report Modal */}
      {generateModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                Generate Sentiment Analysis Report
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600 p-1"
                onClick={() => {
                  setGenerateModalVisible(false);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              {/* Info alert */}
              <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <FileText className="w-5 h-5 shrink-0 mt-0.5 text-blue-600" />
                <div>
                  <p className="font-medium">AI-Powered Analysis</p>
                  <p className="mt-0.5 text-blue-700">
                    This will generate a comprehensive sentiment analysis report using AI to analyze
                    conversations, feedback, and knowledge base documents for the selected event.
                  </p>
                </div>
              </div>

              <form onSubmit={handleGenerateReport} className="space-y-4">
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formClientId}
                      onChange={(e) => {
                        setFormClientId(e.target.value);
                        setSelectedClientId(e.target.value || null);
                        setFormErrors((err) => ({ ...err, clientId: undefined }));
                      }}
                    >
                      <option value="">Select client</option>
                      {clients.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.clientId && (
                      <p className="text-xs text-red-600 mt-1">{formErrors.clientId}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    value={formEventId}
                    disabled={isAdmin && !selectedClientId}
                    onChange={(e) => {
                      setFormEventId(e.target.value);
                      setFormErrors((err) => ({ ...err, eventId: undefined }));
                    }}
                  >
                    <option value="">
                      {isAdmin && !selectedClientId
                        ? 'Select a client first'
                        : 'Select event to analyze'}
                    </option>
                    {events.map((e) => (
                      <option key={e._id} value={e._id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.eventId && (
                    <p className="text-xs text-red-600 mt-1">{formErrors.eventId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="date"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formStartDate}
                      onChange={(e) => setFormStartDate(e.target.value)}
                    />
                    <span className="text-gray-400 text-sm">to</span>
                    <input
                      type="date"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formEndDate}
                      onChange={(e) => setFormEndDate(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Leave empty to analyze last 30 days</p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Generate Report
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setGenerateModalVisible(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Row details modal */}
      {rowDetailsReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Report Row Details</h3>
              <button
                className="text-gray-400 hover:text-gray-600 p-1"
                onClick={() => setRowDetailsReport(null)}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Report ID</p>
                <p className="text-gray-900 font-mono break-all">{rowDetailsReport.reportId}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Knowledge Assistant</p>
                <p className="text-gray-900 break-words">{rowDetailsReport.eventName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <p className="text-gray-800">{rowDetailsReport.status}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Generated</p>
                  <p className="text-gray-800">{dayjs(rowDetailsReport.generatedAt).format('YYYY-MM-DD HH:mm')}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Date Range</p>
                <p className="text-gray-800">
                  {dayjs(rowDetailsReport.analysisDateRange.startDate).format('MMM D, YYYY')} -{' '}
                  {dayjs(rowDetailsReport.analysisDateRange.endDate).format('MMM D, YYYY')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Overall Sentiment</p>
                  <p className="text-gray-800 capitalize">{rowDetailsReport.overallSentiment}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Score</p>
                  <p className="text-gray-800">{rowDetailsReport.sentimentScore.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                className="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => setRowDetailsReport(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {viewModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                <FileText className="w-5 h-5" />
                <span>Sentiment Analysis Report: {selectedReport?.reportId}</span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600 p-1"
                onClick={() => {
                  setViewModalVisible(false);
                  setSelectedReport(null);
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {selectedReport ? (
                <>
                  {/* Report Header Info */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <dl className="grid grid-cols-[160px_1fr] gap-2 text-sm">
                      <dt className="text-gray-500">Knowledge Assistant</dt>
                      <dd className="text-gray-800">{selectedReport.eventName}</dd>
                      <dt className="text-gray-500">Generated</dt>
                      <dd className="text-gray-800">
                        {dayjs(selectedReport.generatedAt).format('YYYY-MM-DD HH:mm')}
                      </dd>
                      <dt className="text-gray-500">Date Range</dt>
                      <dd className="text-gray-800">
                        {dayjs(selectedReport.analysisDateRange.startDate).format('MMM D, YYYY')} -{' '}
                        {dayjs(selectedReport.analysisDateRange.endDate).format('MMM D, YYYY')}
                      </dd>
                      <dt className="text-gray-500">Generated By</dt>
                      <dd className="text-gray-800">{selectedReport.generatedBy}</dd>
                    </dl>
                  </div>

                  {/* Overall Sentiment */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-4">Overall Sentiment</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Sentiment</p>
                        <p
                          className={`text-2xl font-bold flex items-center gap-1 mt-1 ${getSentimentDisplay(selectedReport.overallSentiment).textClass}`}
                        >
                          {getSentimentDisplay(selectedReport.overallSentiment).icon}
                          {getSentimentDisplay(selectedReport.overallSentiment).label}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Sentiment Score</p>
                        <p
                          className={`text-2xl font-bold mt-1 ${getScoreColorClass(selectedReport.sentimentScore)}`}
                        >
                          {selectedReport.sentimentScore.toFixed(2)}
                          <span className="text-sm font-normal text-gray-400 ml-1">/ 1.0</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Score Bar</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              selectedReport.sentimentScore > 0.3
                                ? 'bg-green-600'
                                : selectedReport.sentimentScore < -0.3
                                ? 'bg-red-600'
                                : 'bg-orange-500'
                            }`}
                            style={{
                              width: `${((selectedReport.sentimentScore + 1) / 2) * 100}%`,
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1 text-right">
                          {selectedReport.sentimentScore.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <hr className="border-gray-200 my-3" />
                    <p className="text-sm text-gray-700">{selectedReport.summary}</p>
                  </div>

                  {/* Conversation Analysis */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-4">
                      Conversation Analysis
                    </h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Conversations</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {selectedReport.conversationAnalysis.totalConversations}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Messages</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {selectedReport.conversationAnalysis.totalMessages}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Avg Messages/Conv</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {selectedReport.conversationAnalysis.averageConversationLength.toFixed(1)}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-3 mb-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Sentiment Distribution
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Positive</p>
                          <p className="text-2xl font-bold text-green-600 mt-1">
                            {selectedReport.conversationAnalysis.sentimentDistribution.positive}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Neutral</p>
                          <p className="text-2xl font-bold text-blue-600 mt-1">
                            {selectedReport.conversationAnalysis.sentimentDistribution.neutral}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Negative</p>
                          <p className="text-2xl font-bold text-red-600 mt-1">
                            {selectedReport.conversationAnalysis.sentimentDistribution.negative}
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedReport.conversationAnalysis.commonTopics.length > 0 && (
                      <div className="border-t border-gray-200 pt-3 mb-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Common Topics
                        </p>
                        <ul className="divide-y divide-gray-100">
                          {selectedReport.conversationAnalysis.commonTopics.map((topic, idx) => (
                            <li key={idx} className="py-1.5 flex items-center gap-3 text-sm">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getSentimentDisplay(topic.sentiment).tagClass}`}
                              >
                                {topic.topic}
                              </span>
                              <span className="text-gray-400 text-xs">
                                Frequency: {topic.frequency}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedReport.conversationAnalysis.keyInsights.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Key Insights
                        </p>
                        <ul className="space-y-1">
                          {selectedReport.conversationAnalysis.keyInsights.map((insight, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              • {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Feedback Analysis */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-4">Feedback Analysis</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Feedback</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {selectedReport.feedbackAnalysis.totalFeedback}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Average Rating</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {selectedReport.feedbackAnalysis.averageRating.toFixed(1)}
                          <span className="text-sm font-normal text-gray-400 ml-1">/ 5</span>
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-3 mb-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                        Sentiment Breakdown
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Positive</p>
                          <p className="text-2xl font-bold text-green-600 mt-1">
                            {selectedReport.feedbackAnalysis.sentimentBreakdown.positive}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Neutral</p>
                          <p className="text-2xl font-bold text-blue-600 mt-1">
                            {selectedReport.feedbackAnalysis.sentimentBreakdown.neutral}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Negative</p>
                          <p className="text-2xl font-bold text-red-600 mt-1">
                            {selectedReport.feedbackAnalysis.sentimentBreakdown.negative}
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedReport.feedbackAnalysis.keyIssues.length > 0 && (
                      <div className="border-t border-gray-200 pt-3 mb-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Key Issues
                        </p>
                        <ul className="space-y-1">
                          {selectedReport.feedbackAnalysis.keyIssues.map((issue, idx) => (
                            <li key={idx} className="text-sm text-red-600">
                              • {issue}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedReport.feedbackAnalysis.positiveHighlights.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Positive Highlights
                        </p>
                        <ul className="space-y-1">
                          {selectedReport.feedbackAnalysis.positiveHighlights.map(
                            (highlight, idx) => (
                              <li key={idx} className="text-sm text-green-600">
                                • {highlight}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Knowledge Base Analysis */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-gray-800 mb-4">
                      Knowledge Base Analysis
                    </h4>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500">Total Documents</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {selectedReport.knowledgeBaseAnalysis.totalDocuments}
                      </p>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      <span className="font-medium">Document Relevance: </span>
                      {selectedReport.knowledgeBaseAnalysis.documentRelevance || 'N/A'}
                    </p>
                    {selectedReport.knowledgeBaseAnalysis.documentTopics.length > 0 && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Document Topics
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedReport.knowledgeBaseAnalysis.documentTopics.map((topic, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200"
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recommendations, Trends, Actions */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Recommendations</h4>
                      {selectedReport.recommendations.length > 0 ? (
                        <ul className="space-y-1">
                          {selectedReport.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              • {rec}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No recommendations</p>
                      )}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">
                        Trends Identified
                      </h4>
                      {selectedReport.trendsIdentified.length > 0 ? (
                        <ul className="space-y-1">
                          {selectedReport.trendsIdentified.map((trend, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              • {trend}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No trends identified</p>
                      )}
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Action Items</h4>
                      {selectedReport.actionItems.length > 0 ? (
                        <ul className="space-y-1">
                          {selectedReport.actionItems.map((action, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              • {action}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No action items</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
              {/* Download dropdown in footer */}
              <div className="relative">
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => setViewDownloadDropdownOpen((v) => !v)}
                >
                  <Download className="w-4 h-4" />
                  Download Report
                  <ChevronDown className="w-4 h-4" />
                </button>
                {viewDownloadDropdownOpen && (
                  <div className="absolute bottom-full mb-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                    {(['markdown', 'pdf', 'docx'] as const).map((fmt) => (
                      <button
                        key={fmt}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                        onClick={() => {
                          setViewDownloadDropdownOpen(false);
                          if (selectedReport) handleDownloadReport(selectedReport.reportId, fmt);
                        }}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Download as {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setViewModalVisible(false);
                  setSelectedReport(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentimentAnalysis;
